import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, LinkSimple, PaperPlaneTilt } from 'phosphor-react';
import { toast } from 'sonner';
import { extractApiErrorMessage } from '../../../../utils/ErrorUtils';
import {
  buildEstatutoPublicUrl,
  EstatutoDestino,
  IEstatuto,
  useEstatuto,
} from '../../../../hooks/useEstatuto';
import { ICartorio } from '../../../../hooks/useCartorios';
import { copyToClipboard } from '../utils';
import { CartorioSelect } from './CartorioSelect';
import { EmpresaSelect, IEmpresaLite } from './EmpresaSelect';
import { UsuarioSelect, IUsuarioOption } from './UsuarioSelect';

interface EstatutoCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Quadro pré-selecionado ao abrir (a aba ativa) — task #492.
  destinoInicial?: EstatutoDestino;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EstatutoCreateModal: React.FC<EstatutoCreateModalProps> = ({
  isOpen,
  onClose,
  destinoInicial = 'constituicao',
}) => {
  const queryClient = useQueryClient();
  const { createEstatuto, listAllEstatutos } = useEstatuto();

  const [titulo, setTitulo] = useState('');
  // Quadro de destino (task #492): Constituição (default), Baixa ou Alteração.
  const [destino, setDestino] = useState<EstatutoDestino>(destinoInicial);
  // Alteração (26/10): só o quadro do kanban, sem formulário nem link para o presidente.
  const alteracao = destino === 'alteracao';
  const [expiresAt, setExpiresAt] = useState('');
  const [cartorio, setCartorio] = useState<ICartorio | null>(null);
  // Empresa vinculada (T0-D #502) — opcional; sem ela o estatuto não aparece na Área VIP.
  const [empresa, setEmpresa] = useState<IEmpresaLite | null>(null);
  const [advNome, setAdvNome] = useState('');
  const [advEmail, setAdvEmail] = useState('');
  const [advCpf, setAdvCpf] = useState('');
  const [advOab, setAdvOab] = useState('');
  // Presidente da comissão: recebe o link do formulário por e-mail em "Criar e enviar link".
  const [emailPresidente, setEmailPresidente] = useState('');
  const [emailPresidenteErr, setEmailPresidenteErr] = useState<string | null>(null);
  // Baixa: constituição feita no sistema (turma, presidente e associados vêm prontos).
  const [origemId, setOrigemId] = useState<number | null>(null);
  // Responsáveis (obrigatório, >=1) e supervisores (opcional) — Task #496.
  const [responsaveis, setResponsaveis] = useState<IUsuarioOption[]>([]);
  const [supervisores, setSupervisores] = useState<IUsuarioOption[]>([]);

  const [created, setCreated] = useState<IEstatuto | null>(null);
  const [linkEnviado, setLinkEnviado] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [tituloErr, setTituloErr] = useState<string | null>(null);
  const [cartorioErr, setCartorioErr] = useState<string | null>(null);
  const [advNomeErr, setAdvNomeErr] = useState<string | null>(null);
  const [advEmailErr, setAdvEmailErr] = useState<string | null>(null);
  const [advCpfErr, setAdvCpfErr] = useState<string | null>(null);
  const [advOabErr, setAdvOabErr] = useState<string | null>(null);
  const [responsaveisErr, setResponsaveisErr] = useState<string | null>(null);

  const resetState = () => {
    setTitulo('');
    setDestino(destinoInicial);
    setExpiresAt('');
    setCartorio(null);
    setEmpresa(null);
    setAdvNome('');
    setAdvEmail('');
    setAdvCpf('');
    setAdvOab('');
    setEmailPresidente('');
    setEmailPresidenteErr(null);
    setOrigemId(null);
    setResponsaveis([]);
    setSupervisores([]);
    setCreated(null);
    setLinkEnviado(false);
    setLinkCopied(false);
    setTituloErr(null);
    setCartorioErr(null);
    setAdvNomeErr(null);
    setAdvEmailErr(null);
    setAdvCpfErr(null);
    setAdvOabErr(null);
    setResponsaveisErr(null);
  };

  // Ao (re)abrir o modal, pré-seleciona o destino da aba ativa (task #492). Depende só
  // de isOpen — não sobrescreve a escolha do usuário enquanto o modal está aberto.
  useEffect(() => {
    if (isOpen) setDestino(destinoInicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Mesma consulta (e cache) do quadro: as constituições para a origem da Baixa.
  const { data: todos } = useQuery({
    queryKey: ['estatutos', 'all'],
    queryFn: listAllEstatutos,
    enabled: isOpen && destino === 'baixa',
    staleTime: 30_000,
  });
  const constituicoes = (todos ?? [])
    .filter(e => e.destino !== 'baixa' && e.dados_preenchidos)
    .sort((x, y) => (x.titulo ?? '').localeCompare(y.titulo ?? ''));
  // Trocar a origem troca também o que a anterior preencheu (o resto o admin digitou).
  const escolherOrigem = (id: number | null) => {
    const antes = constituicoes.find(e => e.id === origemId);
    const origem = constituicoes.find(e => e.id === id);
    setOrigemId(id);
    if (!cartorio || cartorio.id === antes?.cartorio?.id) setCartorio(origem?.cartorio ?? null);
    const emailAntes = antes?.email_presidente ?? '';
    if (!emailPresidente.trim() || emailPresidente.trim() === emailAntes)
      setEmailPresidente(origem?.email_presidente ?? '');
  };

  // Campos exigidos pelo backend (422 se faltar) preenchidos? O ADVOGADO é opcional na criação
  // (o envio para assinatura é que o exige) — só título, cartório e responsável bloqueiam o submit.
  const formIncompleto = !titulo.trim() || !cartorio || responsaveis.length === 0;

  const handleClose = () => {
    resetState();
    onClose();
  };

  const createMutation = useMutation({
    mutationFn: (enviarLink: boolean) =>
      createEstatuto({
        titulo: titulo.trim(),
        destino,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        cartorio_id: cartorio?.id ?? null,
        empresa_id: empresa?.id ?? null,
        advogado_nome: advNome.trim() || null,
        advogado_email: advEmail.trim() || null,
        advogado_cpf: advCpf.trim() || null,
        advogado_oab: advOab.trim() || null,
        email_presidente: alteracao ? null : emailPresidente.trim() || null,
        estatuto_origem_id: destino === 'baixa' ? origemId : null,
        responsavel_ids: responsaveis.map(u => u.id),
        supervisor_ids: supervisores.map(u => u.id),
        enviar_link: enviarLink,
      }),
    onSuccess: (res, enviarLink) => {
      queryClient.invalidateQueries({ queryKey: ['estatutos'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
      queryClient.invalidateQueries({ queryKey: ['estatuto-dashboard'] });
      // Sem link para mostrar: a alteração fecha e o card aparece em "Criado".
      if (res.data.destino === 'alteracao') {
        toast.success('Alteração criada!');
        handleClose();
        return;
      }
      setCreated(res.data);
      setLinkEnviado(enviarLink);
      if (!enviarLink) toast.success('Estatuto criado!');
      else if (res.data.email_presidente)
        toast.success(`Estatuto criado e link enviado para ${res.data.email_presidente}`);
      else toast.success('Estatuto criado e link enviado!');
    },
    onError: (err: any) => {
      toast.error(extractApiErrorMessage(err, 'Erro ao criar estatuto'));
    },
  });

  const handleCreate = (enviarLink: boolean) => {
    let hasErr = false;
    if (!titulo.trim()) {
      setTituloErr('Título obrigatório');
      hasErr = true;
    } else {
      setTituloErr(null);
    }
    if (!cartorio) {
      setCartorioErr('Cartório obrigatório');
      hasErr = true;
    } else {
      setCartorioErr(null);
    }
    // Advogado (2º assinante) — opcional na criação; o envio para assinatura exige.
    // Só valida FORMATO se preenchido; vazios são aceitos.
    setAdvNomeErr(null);
    if (advEmail.trim() && !EMAIL_REGEX.test(advEmail.trim())) {
      setAdvEmailErr('Email inválido');
      hasErr = true;
    } else {
      setAdvEmailErr(null);
    }
    setAdvCpfErr(null);
    setAdvOabErr(null);
    if (!alteracao && emailPresidente.trim() && !EMAIL_REGEX.test(emailPresidente.trim())) {
      setEmailPresidenteErr('Email inválido');
      hasErr = true;
    } else {
      setEmailPresidenteErr(null);
    }
    if (responsaveis.length === 0) {
      setResponsaveisErr('Selecione ao menos um responsável');
      hasErr = true;
    } else {
      setResponsaveisErr(null);
    }
    if (hasErr) return;
    createMutation.mutate(enviarLink);
  };

  const publicUrl = created ? buildEstatutoPublicUrl(created.hashed_link) : '';

  const handleCopyLink = async () => {
    if (!publicUrl) return;
    const ok = await copyToClipboard(publicUrl);
    if (ok) {
      setLinkCopied(true);
      toast.success('Link copiado para a área de transferência');
      setTimeout(() => setLinkCopied(false), 2000);
    } else {
      toast.error('Não foi possível copiar — copie manualmente');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent borderRadius="xl">
        <ModalHeader fontSize="md" fontWeight="700" color="gray.800">
          {created ? 'Estatuto criado' : 'Novo estatuto'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {created ? (
            <Flex direction="column" gap={4}>
              <Box
                bg={linkEnviado ? 'green.50' : 'blue.50'}
                borderWidth="1px"
                borderColor={linkEnviado ? 'green.200' : 'blue.200'}
                borderRadius="lg"
                px={3}
                py={2}
              >
                <Text fontSize="sm" color={linkEnviado ? 'green.700' : 'blue.700'} fontWeight="600">
                  {(() => {
                    if (!linkEnviado)
                      return 'Processo criado em "Criado". Envie o link quando quiser para avançar o fluxo.';
                    if (created.email_presidente && created.destino === 'baixa')
                      return `Link do formulário da baixa enviado por e-mail para ${created.email_presidente}.`;
                    if (created.email_presidente)
                      return `Link enviado por e-mail para ${created.email_presidente} — o processo está em "Link Enviado / Aguardando Preenchimento".`;
                    return 'Link marcado como enviado — o processo está em "Link Enviado / Aguardando Preenchimento".';
                  })()}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Compartilhe o link abaixo com o cliente (WhatsApp ou email). Depois que ele
                  preencher, os documentos vão sozinhos para assinatura.
                </Text>
                <FormLabel fontSize="xs" fontWeight="700" color="gray.600">
                  LINK PÚBLICO
                </FormLabel>
                <Flex
                  align="center"
                  bg="gray.50"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="lg"
                  px={3}
                  py={2}
                  gap={2}
                >
                  <LinkSimple size={16} color="#1B88F5" />
                  <Text
                    fontSize="xs"
                    color="gray.700"
                    flex={1}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {publicUrl}
                  </Text>
                  <Tooltip label={linkCopied ? 'Copiado!' : 'Copiar link'} fontSize="xs">
                    <IconButton
                      aria-label="Copiar link"
                      size="sm"
                      variant="ghost"
                      icon={
                        linkCopied ? (
                          <Check size={16} weight="bold" color="#16A34A" />
                        ) : (
                          <Copy size={16} weight="bold" />
                        )
                      }
                      onClick={handleCopyLink}
                    />
                  </Tooltip>
                </Flex>
              </Box>
            </Flex>
          ) : (
            <Flex direction="column" gap={4}>
              <FormControl isRequired isInvalid={!!tituloErr}>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Título
                </FormLabel>
                <Input
                  placeholder="Ex: Comissão de Formatura — Turma 10 Medicina"
                  value={titulo}
                  onChange={e => setTitulo(e.target.value)}
                  autoFocus
                />
                {tituloErr && <FormErrorMessage>{tituloErr}</FormErrorMessage>}
              </FormControl>

              {/* Destino (task #492): Constituição (criação) ou Baixa (encerramento) */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Destino
                </FormLabel>
                <Flex bg="gray.100" borderRadius="lg" p={1} gap={1}>
                  {(
                    [
                      { key: 'constituicao', label: 'Constituição' },
                      { key: 'baixa', label: 'Baixa' },
                      { key: 'alteracao', label: 'Alteração' },
                    ] as { key: EstatutoDestino; label: string }[]
                  ).map(opt => {
                    const active = destino === opt.key;
                    return (
                      <Button
                        key={opt.key}
                        size="sm"
                        flex="1"
                        variant="ghost"
                        bg={active ? 'white' : 'transparent'}
                        color={active ? 'purple.600' : 'gray.500'}
                        boxShadow={active ? 'sm' : 'none'}
                        fontWeight={active ? '700' : '600'}
                        _hover={{ bg: active ? 'white' : 'gray.50' }}
                        onClick={() => setDestino(opt.key)}
                      >
                        {opt.label}
                      </Button>
                    );
                  })}
                </Flex>
              </FormControl>

              {destino === 'baixa' && (
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                    Constituição desta comissão no sistema (opcional)
                  </FormLabel>
                  <Select
                    placeholder="Nenhuma: o presidente preenche tudo"
                    value={origemId ?? ''}
                    onChange={e => escolherOrigem(e.target.value ? Number(e.target.value) : null)}
                  >
                    {constituicoes.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.titulo ?? `Estatuto #${e.id}`}
                        {(e.dados_preenchidos as any)?.pessoas
                          ? ''
                          : ' (sem a lista de fundadores)'}
                      </option>
                    ))}
                  </Select>
                  <Text fontSize="11px" color="gray.500" mt={1}>
                    Turma, presidente e associados vêm prontos para o formulário da baixa (as
                    constituições sem a lista de fundadores trazem só a turma e o presidente).
                  </Text>
                </FormControl>
              )}

              {alteracao ? (
                <Text fontSize="11px" color="gray.500" mt={-2}>
                  A alteração ainda não tem formulário: o card nasce em &quot;Criado&quot; e a
                  equipe anexa os documentos e move o card pelo quadro.
                </Text>
              ) : (
                <FormControl isInvalid={!!emailPresidenteErr}>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                    E-mail do presidente da comissão (opcional)
                  </FormLabel>
                  <Input
                    type="email"
                    placeholder="presidente@exemplo.com"
                    value={emailPresidente}
                    onChange={e => {
                      setEmailPresidente(e.target.value);
                      setEmailPresidenteErr(null);
                    }}
                  />
                  {emailPresidenteErr ? (
                    <FormErrorMessage>{emailPresidenteErr}</FormErrorMessage>
                  ) : (
                    <Text fontSize="11px" color="gray.500" mt={1}>
                      {destino === 'baixa'
                        ? 'Em "Criar e enviar link", ele recebe por e-mail o formulário da baixa (convocação e assembleia, uma parte depois da outra). Ao enviar, os 4 documentos vão para assinatura.'
                        : 'Em "Criar e enviar link", ele recebe por e-mail o formulário com os dados para o estatuto, a ata, a relação dos fundadores, a lista de presença e o requerimento. Ao enviar, os documentos vão para assinatura.'}
                    </Text>
                  )}
                </FormControl>
              )}

              {/* Empresa vinculada (task #502) — para o estatuto aparecer na Área VIP */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Empresa (opcional)
                </FormLabel>
                <EmpresaSelect value={empresa} onChange={setEmpresa} />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Expira em (opcional)
                </FormLabel>
                <Input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={e => setExpiresAt(e.target.value)}
                />
                <Text fontSize="11px" color="gray.500" mt={1}>
                  {alteracao ? 'Prazo do processo.' : 'Data limite para o cliente preencher.'} Deixe
                  em branco se não houver limite.
                </Text>
              </FormControl>

              <FormControl isRequired isInvalid={!!cartorioErr}>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Cartório
                </FormLabel>
                <CartorioSelect
                  value={cartorio}
                  onChange={c => {
                    setCartorio(c);
                    if (c) setCartorioErr(null);
                  }}
                />
                {cartorioErr && <FormErrorMessage>{cartorioErr}</FormErrorMessage>}
              </FormControl>

              <FormControl isRequired isInvalid={!!responsaveisErr}>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Responsável(eis)
                </FormLabel>
                <UsuarioSelect
                  value={responsaveis}
                  onChange={us => {
                    setResponsaveis(us);
                    if (us.length > 0) setResponsaveisErr(null);
                  }}
                  isInvalid={!!responsaveisErr}
                  placeholder="Buscar responsável pelo nome…"
                />
                {responsaveisErr && <FormErrorMessage>{responsaveisErr}</FormErrorMessage>}
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Supervisor(es){' '}
                  <Text as="span" fontSize="xs" color="gray.400" fontWeight="400">
                    (opcional)
                  </Text>
                </FormLabel>
                <UsuarioSelect
                  value={supervisores}
                  onChange={setSupervisores}
                  placeholder="Buscar supervisor pelo nome…"
                />
              </FormControl>

              <Divider />

              <Box>
                <Text fontSize="sm" fontWeight="700" color="gray.700" mb={1}>
                  Advogado
                </Text>
                <Text fontSize="11px" color="gray.500" mb={3}>
                  {alteracao
                    ? 'Opcional: fica nos dados do processo e pode ser preenchido depois, no card.'
                    : 'Assina o Estatuto e as Atas. Preencha aqui ou no card antes de o presidente enviar o formulário: sem o advogado, os documentos não vão para assinatura e quem criou o card recebe o aviso.'}
                </Text>
                <Flex direction="column" gap={3}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <FormControl isInvalid={!!advNomeErr}>
                      <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                        Nome
                      </FormLabel>
                      <Input
                        size="sm"
                        value={advNome}
                        onChange={e => {
                          setAdvNome(e.target.value);
                          if (e.target.value.trim()) setAdvNomeErr(null);
                        }}
                      />
                      {advNomeErr && <FormErrorMessage>{advNomeErr}</FormErrorMessage>}
                    </FormControl>
                    <FormControl isInvalid={!!advEmailErr}>
                      <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                        Email
                      </FormLabel>
                      <Input
                        size="sm"
                        type="email"
                        value={advEmail}
                        onChange={e => {
                          setAdvEmail(e.target.value);
                          if (e.target.value.trim()) setAdvEmailErr(null);
                        }}
                      />
                      {advEmailErr && <FormErrorMessage>{advEmailErr}</FormErrorMessage>}
                    </FormControl>
                    <FormControl isInvalid={!!advCpfErr}>
                      <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                        CPF
                      </FormLabel>
                      <Input
                        size="sm"
                        value={advCpf}
                        onChange={e => {
                          setAdvCpf(e.target.value);
                          if (e.target.value.trim()) setAdvCpfErr(null);
                        }}
                      />
                      {advCpfErr && <FormErrorMessage>{advCpfErr}</FormErrorMessage>}
                    </FormControl>
                    <FormControl isInvalid={!!advOabErr}>
                      <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                        OAB
                      </FormLabel>
                      <Input
                        size="sm"
                        value={advOab}
                        onChange={e => {
                          setAdvOab(e.target.value);
                          if (e.target.value.trim()) setAdvOabErr(null);
                        }}
                      />
                      {advOabErr && <FormErrorMessage>{advOabErr}</FormErrorMessage>}
                    </FormControl>
                  </SimpleGrid>
                </Flex>
              </Box>
            </Flex>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          {created ? (
            <Button colorScheme="blue" onClick={handleClose}>
              Fechar
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                variant={alteracao ? 'solid' : 'outline'}
                colorScheme="blue"
                onClick={() => handleCreate(false)}
                isLoading={createMutation.isPending && createMutation.variables === false}
                isDisabled={createMutation.isPending || formIncompleto}
              >
                Criar
              </Button>
              {!alteracao && (
                <Button
                  colorScheme="blue"
                  leftIcon={<PaperPlaneTilt size={14} weight="bold" />}
                  onClick={() => handleCreate(true)}
                  isLoading={createMutation.isPending && createMutation.variables === true}
                  isDisabled={createMutation.isPending || formIncompleto}
                >
                  Criar e enviar link
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
